import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCabDashboard } from '../../../api/cabsAdmin';

const emptySummary = {
  totalVehicles: 0,
  activeVehicles: 0,
  totalCategories: 0,
  totalSeatConfigurations: 0,
  totalLocations: 0,
  totalRoutes: 0,
  totalPricingRules: 0,
  totalTransportPackages: 0,
  totalEnquiries: 0,
  pendingEnquiries: 0,
};

const quickLinks = [
  { label: 'Vehicles', to: '/admin/cabs/vehicles' },
  { label: 'Pricing Rules', to: '/admin/cabs/pricing-rules' },
  { label: 'Transport Packages', to: '/admin/cabs/transport-packages' },
  { label: 'Enquiries', to: '/admin/cabs/enquiries' },
];

const AdminCabsDashboard = () => {
  const [data, setData] = useState({
    summary: emptySummary,
    recentVehicles: [],
    recentEnquiries: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getCabDashboard();
        if (!active) return;
        setData(res?.data || res || { summary: emptySummary, recentVehicles: [], recentEnquiries: [] });
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Failed to load dashboard');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const cards = [
    ['Vehicles', data.summary.totalVehicles],
    ['Active fleet', data.summary.activeVehicles],
    ['Routes', data.summary.totalRoutes],
    ['Pricing rules', data.summary.totalPricingRules],
    ['Transport packages', data.summary.totalTransportPackages],
    ['Pending enquiries', data.summary.pendingEnquiries],
  ];

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-600">Cab Services</p>
        <h1 className="text-2xl font-semibold text-slate-900">Cab Services Dashboard</h1>
        <p className="text-sm text-slate-600">Overview of fleet readiness, routes, pricing, and enquiry flow.</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs uppercase tracking-[0.16em] text-slate-500">{label}</div>
            <div className="mt-2 text-3xl font-semibold text-slate-900">
              {loading ? '...' : value}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {quickLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700 no-underline hover:bg-slate-50"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">Recent vehicles</h2>
          <div className="mt-3 space-y-3">
            {!loading && data.recentVehicles.length === 0 && (
              <div className="text-sm text-slate-500">No vehicles yet.</div>
            )}
            {data.recentVehicles.map((vehicle) => (
              <div key={vehicle.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3">
                <div className="font-semibold text-slate-900">{vehicle.name}</div>
                <div className="text-xs text-slate-500">
                  {vehicle.category?.name || 'Uncategorized'} · {vehicle.seatConfig?.seatCapacity || '-'} seats
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">Recent enquiries</h2>
          <div className="mt-3 space-y-3">
            {!loading && data.recentEnquiries.length === 0 && (
              <div className="text-sm text-slate-500">No enquiries yet.</div>
            )}
            {data.recentEnquiries.map((enquiry) => (
              <div key={enquiry.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3">
                <div className="font-semibold text-slate-900">
                  {enquiry.customerName || enquiry.fullName || 'Guest'}
                </div>
                <div className="text-xs text-slate-500">
                  {enquiry.pickupLocation?.name || '-'} to {enquiry.dropLocation?.name || '-'}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-slate-400">
                  {enquiry.status || 'NEW'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCabsDashboard;
